export type SearchCandidate<T> = {
  key: string;
  title: string;
  primary: string[];
  secondary: string[];
  value: T;
};

type RankedCandidate<T> = SearchCandidate<T> & { score: number };

export function isPublishedSite<T extends { status?: string }>(site: T | null | undefined): site is T {
  return site?.status === "published";
}

function normalize(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .trim();
}

function fieldScore(value: string, term: string, weight: number) {
  const normalized = normalize(value);
  if (!normalized.includes(term)) return 0;
  if (normalized === term) return weight + 40;
  if (normalized.startsWith(term)) return weight + 30;
  if (normalized.split(/[^a-z0-9]+/).some(word => word.startsWith(term))) return weight + 20;
  return weight + 10;
}

export function rankSearchCandidates<T>(candidates: SearchCandidate<T>[], rawTerm: string, limit = 8): RankedCandidate<T>[] {
  const term = normalize(rawTerm);
  if (term.length < 2 || limit <= 0) return [];

  return candidates
    .map(candidate => ({
      ...candidate,
      score: Math.max(
        ...candidate.primary.map(value => fieldScore(value, term, 100)),
        ...candidate.secondary.map(value => fieldScore(value, term, 40)),
      ),
    }))
    .filter(candidate => candidate.score > 0)
    .sort((left, right) => right.score - left.score
      || left.title.localeCompare(right.title, "pt-BR")
      || left.key.localeCompare(right.key, "pt-BR"))
    .slice(0, limit);
}
